import axios from 'utils/axios';
import { normalizeWaRiskPreviewResult, normalizeWaRiskQuestionsPayload } from 'utils/whatsapp-risk-assessment';
import type {
  WaBillingBalance,
  WaBillingConfig,
  WaBillingUsageEntry,
  WaChat,
  WaGroupsListResponse,
  WaCreateInstancePayload,
  WaCreateInstanceResponse,
  WaInstance,
  WaInstanceAutomationConfig,
  WaInstanceAutomationPayload,
  WaInstanceConnectionStatus,
  WaInstanceRiskAssessmentRecord,
  WaInstanceQrCode,
  WaRiskPreviewResult,
  WaRiskQuestionsPayload,
  WaSendTextResult,
  WaThreadMessage,
  WaScheduleMessagePayload,
  WaRescheduleMessagePayload,
  WaScheduledListQuery,
  WaScheduledListResponse,
  WaScheduledMessage,
  WaScheduledOverviewQuery,
  WaAgentRun,
  WaAgentRunActionResponse,
  WaAgentRunListQuery,
  WaAuditLog,
  WaPipelineHealth,
  WaConversationSummary,
  WaSummarizeConversationPayload,
  WaChatSearchResult,
  WaPagedResponse,
  WaSingleResponse
} from 'types/whatsapp';

// ── Instances ─────────────────────────────────────────────────────────────────

export async function listInstances(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<WaPagedResponse<WaInstance>> {
  const { data } = await axios.get<WaPagedResponse<WaInstance>>('/whatsapp/instances', { params });
  return data;
}

function normalizeCreateInstanceResponse(raw: unknown): WaCreateInstanceResponse {
  const body = (raw ?? {}) as Record<string, unknown>;
  const nested = body.data as Record<string, unknown> | undefined;
  const instance = (nested?.instance ?? nested ?? body.data) as WaCreateInstanceResponse['data'];
  const riskAssessment = (body.riskAssessment ?? nested?.riskAssessment) as WaCreateInstanceResponse['riskAssessment'];
  return {
    message: String(body.message ?? ''),
    data: instance,
    riskAssessment: riskAssessment ?? undefined
  };
}

export async function createInstance(payload: WaCreateInstancePayload): Promise<WaCreateInstanceResponse> {
  const { data } = await axios.post('/whatsapp/instances', payload);
  return normalizeCreateInstanceResponse(data);
}

// ── Risk assessment ───────────────────────────────────────────────────────────

export async function getWaRiskAssessmentQuestions(): Promise<WaSingleResponse<WaRiskQuestionsPayload>> {
  const { data } = await axios.get('/whatsapp/risk-assessment/questions');
  return {
    message: typeof (data as { message?: string })?.message === 'string' ? (data as { message: string }).message : 'OK',
    data: normalizeWaRiskQuestionsPayload(data)
  };
}

export async function previewWaRiskAssessment(payload: {
  answers: { questionId: string; value: string }[];
}): Promise<WaSingleResponse<WaRiskPreviewResult>> {
  const { data } = await axios.post('/whatsapp/risk-assessment/preview', payload);
  return {
    message: typeof (data as { message?: string })?.message === 'string' ? (data as { message: string }).message : 'OK',
    data: normalizeWaRiskPreviewResult(data)
  };
}

type WaUnreadCounts = { total: number; byInstance: Record<string, number> };

/** Contagem de não-lidas do WhatsApp: total + por instância. */
export async function getWhatsAppUnreadCount(): Promise<WaUnreadCounts> {
  const { data } = await axios.get<{ data: WaUnreadCounts }>('/whatsapp/unread/count');
  return data.data;
}

/** Marca uma conversa como lida (zera o contador de não-lidas dela). */
export async function markWhatsAppChatRead(instanceId: string, chatJid: string): Promise<WaUnreadCounts> {
  const { data } = await axios.post<{ data: WaUnreadCounts }>('/whatsapp/unread/read', { instanceId, chatJid });
  return data.data;
}

export async function getInstanceRiskAssessment(
  instanceId: string
): Promise<WaSingleResponse<WaInstanceRiskAssessmentRecord>> {
  const { data } = await axios.get<WaSingleResponse<WaInstanceRiskAssessmentRecord>>(
    `/whatsapp/instances/${instanceId}/risk-assessment`
  );
  return data;
}

export async function getInstanceQrCode(id: string): Promise<WaSingleResponse<WaInstanceQrCode>> {
  const { data } = await axios.get<WaSingleResponse<WaInstanceQrCode>>(`/whatsapp/instances/${id}/qrcode`);
  return data;
}

export async function getInstanceStatus(id: string): Promise<WaSingleResponse<WaInstanceConnectionStatus>> {
  const { data } = await axios.get<WaSingleResponse<WaInstanceConnectionStatus>>(`/whatsapp/instances/${id}/status`);
  return data;
}

export async function updateInstance(
  id: string,
  payload: {
    displayName?: string;
  }
): Promise<WaSingleResponse<WaInstance>> {
  const { data } = await axios.patch<WaSingleResponse<WaInstance>>(`/whatsapp/instances/${id}`, payload);
  return data;
}

export async function deleteInstance(id: string): Promise<{ message: string }> {
  const { data } = await axios.delete<{ message: string }>(`/whatsapp/instances/${id}`);
  return data;
}

export async function disconnectInstance(id: string): Promise<WaSingleResponse<WaInstance>> {
  const { data } = await axios.post<WaSingleResponse<WaInstance>>(`/whatsapp/instances/${id}/disconnect`);
  return data;
}

export async function reconnectInstance(id: string): Promise<WaSingleResponse<WaInstance>> {
  const { data } = await axios.post<WaSingleResponse<WaInstance>>(`/whatsapp/instances/${id}/reconnect`);
  return data;
}

// ── Automação por instância ───────────────────────────────────────────────────

export async function getInstanceAutomation(
  instanceId: string
): Promise<WaSingleResponse<WaInstanceAutomationConfig>> {
  const { data } = await axios.get<WaSingleResponse<WaInstanceAutomationConfig>>(
    `/whatsapp/instances/${instanceId}/automation`
  );
  return data;
}

export async function updateInstanceAutomation(
  instanceId: string,
  payload: WaInstanceAutomationPayload
): Promise<WaSingleResponse<WaInstanceAutomationConfig>> {
  const { data } = await axios.put<WaSingleResponse<WaInstanceAutomationConfig>>(
    `/whatsapp/instances/${instanceId}/automation`,
    payload
  );
  return data;
}

// ── Inbox (conversas por instância) ───────────────────────────────────────────

export async function listInstanceChats(
  instanceId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    group?: 'only' | 'exclude';
  }
): Promise<WaPagedResponse<WaChat>> {
  const { data } = await axios.get<WaPagedResponse<WaChat>>(`/whatsapp/instances/${instanceId}/chats`, { params });
  return data;
}

export async function listInstanceGroups(
  instanceId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    subject?: string;
    getParticipants?: boolean;
  }
): Promise<WaGroupsListResponse> {
  const { data } = await axios.get<WaGroupsListResponse>(`/whatsapp/instances/${instanceId}/groups`, { params });
  return data;
}

export async function refreshInstanceGroups(instanceId: string): Promise<{ message: string }> {
  const { data } = await axios.post<{ message: string }>(`/whatsapp/instances/${instanceId}/groups/refresh`);
  return data;
}

export async function listInstanceMessages(
  instanceId: string,
  params: {
    chatJid: string;
    page?: number;
    limit?: number;
    before?: string;
    direction?: string;
    type?: string;
    sortOrder?: 'asc' | 'desc';
  }
): Promise<WaPagedResponse<WaThreadMessage>> {
  const { data } = await axios.get<WaPagedResponse<WaThreadMessage>>(
    `/whatsapp/instances/${instanceId}/messages`,
    { params }
  );
  return data;
}

// ── Envio (inbox) ─────────────────────────────────────────────────────────────

/** POST /whatsapp/instances/:id/messages/text — persistência via webhook message.sent + WS. */
export async function sendInstanceText(
  instanceId: string,
  payload: { to: string; text: string }
): Promise<WaSingleResponse<WaSendTextResult>> {
  const { data } = await axios.post<WaSingleResponse<WaSendTextResult>>(
    `/whatsapp/instances/${instanceId}/messages/text`,
    payload
  );
  return data;
}

export async function sendInstanceAudio(
  instanceId: string,
  payload: { to: string; file: File }
): Promise<WaSingleResponse<WaThreadMessage>> {
  const form = new FormData();
  form.append('to', payload.to);
  form.append('file', payload.file, payload.file.name || 'audio');

  const { data } = await axios.post<WaSingleResponse<WaThreadMessage>>(
    `/whatsapp/instances/${instanceId}/messages/audio`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );
  return data;
}

export async function sendInstanceImage(
  instanceId: string,
  payload: { to: string; file: File; caption?: string }
): Promise<WaSingleResponse<WaThreadMessage>> {
  const form = new FormData();
  form.append('to', payload.to);
  form.append('file', payload.file, payload.file.name || 'image');
  if (payload.caption?.trim()) {
    form.append('caption', payload.caption.trim());
  }

  const { data } = await axios.post<WaSingleResponse<WaThreadMessage>>(
    `/whatsapp/instances/${instanceId}/messages/image`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function sendInstanceVideo(
  instanceId: string,
  payload: { to: string; file: File; caption?: string }
): Promise<WaSingleResponse<WaThreadMessage>> {
  const form = new FormData();
  form.append('to', payload.to);
  form.append('file', payload.file, payload.file.name || 'video');
  if (payload.caption?.trim()) {
    form.append('caption', payload.caption.trim());
  }

  const { data } = await axios.post<WaSingleResponse<WaThreadMessage>>(
    `/whatsapp/instances/${instanceId}/messages/video`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function sendInstanceDocument(
  instanceId: string,
  payload: { to: string; file: File; filename?: string; caption?: string }
): Promise<WaSingleResponse<WaThreadMessage>> {
  const form = new FormData();
  form.append('to', payload.to);
  const filename = payload.filename?.trim() || payload.file.name || 'document';
  form.append('file', payload.file, filename);
  form.append('filename', filename);
  if (payload.caption?.trim()) {
    form.append('caption', payload.caption.trim());
  }

  const { data } = await axios.post<WaSingleResponse<WaThreadMessage>>(
    `/whatsapp/instances/${instanceId}/messages/document`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );
  return data;
}

export async function fetchMessageMediaBlob(instanceId: string, messageId: string): Promise<Blob> {
  const { data } = await axios.get(`/whatsapp/instances/${instanceId}/messages/${messageId}/media`, {
    responseType: 'blob'
  });
  return data as Blob;
}

/**
 * Resumo de conversa por IA (para repasse a outro setor). Rule: whatsapp.messages.read.
 * Sem `messageIds` → resume a conversa toda; com `messageIds` (UUIDs) → só as selecionadas.
 */
export async function summarizeConversation(
  instanceId: string,
  payload: WaSummarizeConversationPayload
): Promise<WaSingleResponse<WaConversationSummary>> {
  const { data } = await axios.post<WaSingleResponse<WaConversationSummary>>(`/whatsapp/instances/${instanceId}/messages/summary`, payload);
  return data;
}

/**
 * Busca contatos/grupos já existentes (por nome ou número) para o seletor de
 * encaminhar. `q` vazio devolve os chats mais recentes. Rule: whatsapp.messages.read.
 */
export async function searchInstanceChats(instanceId: string, q?: string, limit = 20): Promise<WaChatSearchResult[]> {
  const { data } = await axios.get<WaSingleResponse<WaChatSearchResult[]>>(`/whatsapp/instances/${instanceId}/chats/search`, {
    params: { q: q?.trim() || undefined, limit }
  });
  return data.data ?? [];
}

export async function fetchMessageMediaBlobByUrl(mediaUrl: string): Promise<Blob> {
  const { data } = await axios.get(mediaUrl, {
    responseType: 'blob',
    /** Permite cache HTTP do browser (200 disk cache / 304 revalidação). */
    headers: { Accept: '*/*' }
  });
  return data as Blob;
}

// ── Billing ───────────────────────────────────────────────────────────────────

export async function getWhatsAppBillingConfig(): Promise<WaSingleResponse<WaBillingConfig>> {
  const { data } = await axios.get<WaSingleResponse<WaBillingConfig>>('/whatsapp/billing/config');
  return data;
}

export async function updateWhatsAppBillingConfig(
  payload: Partial<
    Pick<
      WaBillingConfig,
      | 'messageMode'
      | 'costPerMessageUsd'
      | 'costPerConversationUsd'
      | 'costPerInstanceMonthlyUsd'
      | 'maxMessagesPerConversation'
      | 'conversationWindowHours'
    >
  >
): Promise<WaSingleResponse<WaBillingConfig>> {
  const { data } = await axios.patch<WaSingleResponse<WaBillingConfig>>('/whatsapp/billing/config', payload);
  return data;
}

export async function getWhatsAppBillingBalance(): Promise<WaSingleResponse<WaBillingBalance>> {
  const { data } = await axios.get<WaSingleResponse<WaBillingBalance>>('/whatsapp/billing/balance');
  return data;
}

/** Top-up do orçamento de IA do usuário (mesmo fluxo que AiBudgetService.rechargeUserBudget). */
export async function creditWhatsAppUser(payload: {
  userId?: string;
  amountUsd: number;
  periodDays?: number;
  description?: string;
}): Promise<WaSingleResponse<WaBillingBalance>> {
  const { data } = await axios.post<WaSingleResponse<WaBillingBalance>>('/whatsapp/billing/credit', payload);
  return data;
}

export async function listWhatsAppBillingUsage(params?: {
  limit?: number;
  before?: string;
}): Promise<WaPagedResponse<WaBillingUsageEntry>> {
  const { data } = await axios.get<WaPagedResponse<WaBillingUsageEntry>>('/whatsapp/billing/usage', { params });
  return data;
}

// ── Mensagens agendadas (scheduled-messages) ──────────────────────────────────

const scheduledMessagesBase = (instanceId: string) => `/whatsapp/instances/${instanceId}/scheduled-messages`;

/** GET — ordenado por sendAt ASC. Rule: whatsapp.messages.read. */
export async function listScheduledMessages(instanceId: string, query: WaScheduledListQuery = {}): Promise<WaScheduledListResponse> {
  const { data } = await axios.get<WaScheduledListResponse>(scheduledMessagesBase(instanceId), { params: query });
  return data;
}

/** GET company-wide — agendados de todas as instâncias da empresa (filtro opcional por instância). Rule: whatsapp.messages.read. */
export async function listAllScheduledMessages(query: WaScheduledOverviewQuery = {}): Promise<WaScheduledListResponse> {
  const { data } = await axios.get<WaScheduledListResponse>('/whatsapp/scheduled-messages', { params: query });
  return data;
}

/** POST — agenda um envio futuro. Rule: whatsapp.messages.schedule. */
export async function scheduleMessage(
  instanceId: string,
  payload: WaScheduleMessagePayload
): Promise<WaSingleResponse<WaScheduledMessage>> {
  const { data } = await axios.post<WaSingleResponse<WaScheduledMessage>>(scheduledMessagesBase(instanceId), payload);
  return data;
}

/** PATCH — só enquanto pending. Rule: whatsapp.messages.schedule. */
export async function rescheduleMessage(
  instanceId: string,
  sid: string,
  payload: WaRescheduleMessagePayload
): Promise<WaSingleResponse<WaScheduledMessage>> {
  const { data } = await axios.patch<WaSingleResponse<WaScheduledMessage>>(`${scheduledMessagesBase(instanceId)}/${sid}`, payload);
  return data;
}

/** DELETE — só enquanto pending. Rule: whatsapp.messages.schedule. */
export async function cancelScheduledMessage(instanceId: string, sid: string): Promise<WaSingleResponse<WaScheduledMessage>> {
  const { data } = await axios.delete<WaSingleResponse<WaScheduledMessage>>(`${scheduledMessagesBase(instanceId)}/${sid}`);
  return data;
}

// ── Caixa de propostas da IA + auditoria (itens 8/10/12) ──────────────────────

/** GET — lista de agent runs. Rule: whatsapp.automations.read. */
export async function listAgentRuns(params: WaAgentRunListQuery = {}): Promise<WaPagedResponse<WaAgentRun>> {
  const { data } = await axios.get<WaPagedResponse<WaAgentRun>>('/whatsapp/agent-runs', { params });
  return data;
}

/** GET — detalhe de uma run. Rule: whatsapp.automations.read. */
export async function getAgentRun(id: string): Promise<WaSingleResponse<WaAgentRun>> {
  const { data } = await axios.get<WaSingleResponse<WaAgentRun>>(`/whatsapp/agent-runs/${id}`);
  return data;
}

/** GET — histórico de auditoria da run (item 12). Rule: whatsapp.automations.read. */
export async function getAgentRunAudit(id: string): Promise<WaSingleResponse<WaAuditLog[]>> {
  const { data } = await axios.get<WaSingleResponse<WaAuditLog[]>>(`/whatsapp/agent-runs/${id}/audit`);
  return data;
}

/** POST — aprova e envia (omita replyText para enviar como veio). Rule: whatsapp.automations.trigger. */
export async function approveAgentRun(id: string, payload: { replyText?: string } = {}): Promise<WaAgentRunActionResponse> {
  const { data } = await axios.post<WaAgentRunActionResponse>(`/whatsapp/agent-runs/${id}/approve`, payload);
  return data;
}

/** POST — descarta a proposta. Rule: whatsapp.automations.trigger. */
export async function discardAgentRun(id: string): Promise<WaSingleResponse<WaAgentRun>> {
  const { data } = await axios.post<WaSingleResponse<WaAgentRun>>(`/whatsapp/agent-runs/${id}/discard`);
  return data;
}

// ── Observabilidade do pipeline (item 13) ─────────────────────────────────────

/** GET — saúde do pipeline (filas + guardrails de custo + estatísticas do agente). Rule: whatsapp.automations.read. */
export async function getPipelineHealth(windowDays = 7): Promise<WaSingleResponse<WaPipelineHealth>> {
  const { data } = await axios.get<WaSingleResponse<WaPipelineHealth>>('/whatsapp/observability/health', {
    params: { windowDays }
  });
  return data;
}
